import React, { useEffect, useRef, useState, useCallback } from 'react';
import {Elements} from '@stripe/react-stripe-js';
import {loadStripe} from '@stripe/stripe-js';
import {getDataWithFetch, getLocalStorage, setLocalStorage, getTagLabelInfo, getIpInfo} from "./components/Utils";
import HomePage from './pages/HomePage';
import TopStories from './pages/TopStories';
import AdminPage from './pages/AdminPage';
import ApplicationData from './components/ApplicationData';
import ApplicationContext from './components/ApplicationContext';
import Invest from './pages/Invest';
import FAQ from './pages/FAQ';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

const App = ({BASE_URL}) => {
	// process.env.PUBLIC_URL = "TEST";
	const obtainedNavItems = useRef(false);
    const [data, setData] = useState(ApplicationData);

	const renderPage = () => {
		switch (data.currentPage) {
			case 'login':
				return <LoginPage setData={setData} />;
			case 'signup':
				return <SignupPage setData={setData} />;
			case 'admin':
				return <AdminPage setData={setData} />;
			case 'topstories':
				return <TopStories setData={setData} />;
			case 'faq':
				return <FAQ setData={setData} />;
			case 'invest':
				return <Invest setData={setData} />;
			default:
				return <HomePage setData={setData} />;
		}
	};

	const [, setChoicePairsList] = useState();

	// Disable this only for temporary release to include all the 
	// new code changes minus monetization.
	const [enableStripe] = useState(ApplicationData.enableStripe);
	const [stripePromise, setStripePromise] = useState(null);

	useEffect(() => {
        (async() => {
			if (obtainedNavItems.current === false) {
				obtainedNavItems.current = true;

				let queryParams = new URLSearchParams(window.location.search);
				ApplicationData.enableAdmin = queryParams.has("enableAdmin");

				// console.log("DEBUG", "App:  ApplicationData.enableAdmin", ApplicationData.enableAdmin);

				// If the query parameter is set to to enableAdmin=true then we want the admin site to load.
				if (ApplicationData.enableAdmin) {
					const username = getLocalStorage("username");
					// console.log("DEBUG", `App:  username = ${username}`);

					if (username === 'admin') {
						setLocalStorage('currentPage', 'admin');
						ApplicationData.currentPage = 'admin';
						setData((prev) => ({ ...prev, currentPage: 'admin' }))
					}
					else {
						setLocalStorage('currentPage', 'login');
						ApplicationData.currentPage = 'login';
						setData((prev) => ({ ...prev, currentPage: 'login' }))
					}
				}
				else {
					// Then only if it was previously set to admin do we want to reset it to home.
					if (getLocalStorage('currentPage') === 'admin' || getLocalStorage('currentPage') === 'login') {
						setLocalStorage('currentPage', 'home');
						ApplicationData.currentPage = 'home';
						setData((prev) => ({ ...prev, currentPage: 'home' }))
					}
				}

				// console.log(`DEBUG:  App - BASE_URL = ${BASE_URL}`);
				if (BASE_URL) {
                    // console.log('DEBUG', `BASE_URL = `, BASE_URL);
					await getIpInfo(BASE_URL)

                    let url, res;

					url = `${ApplicationData.BASE_URL}index.php/misc/get_site_closed_flag`;
					// console.log('DEBUG:  Admin - ', url);
					const json = await getDataWithFetch(url);
					// console.log('DEBUG:  App - get_site_closed_flag response:', json);
					ApplicationData.siteClosedFlag = json?.SITE_CLOSED;
					ApplicationData.siteClosedMessage = json?.SITE_CLOSED_MESSAGE;
					ApplicationData.siteClosedVideoUrls = json?.SITE_CLOSED_VIDEO_URLS || [];

					// console.log('DEBUG:  App - siteClosedFlag', ApplicationData.siteClosedFlag);
					// console.log('DEBUG:  App - siteClosedMessage', ApplicationData.siteClosedMessage);
					// console.log('DEBUG:  App - siteClosedVideoUrls', ApplicationData.siteClosedVideoUrls);

					url = BASE_URL + 'index.php/link_data/get_link_data';
					// console.log('DEBUG:', url);
					res = await getDataWithFetch(url);
					ApplicationData.linkData = res;

					setData((prev) => {
						const newData = {
							...prev,
							siteClosedFlag: json?.SITE_CLOSED,
							siteClosedMessage: json?.SITE_CLOSED_MESSAGE,
							siteClosedVideoUrls: json?.SITE_CLOSED_VIDEO_URLS || [],
							linkData: res,
						};
						// console.log('DEBUG:  App - Setting data with siteClosedVideoUrls:', newData.siteClosedVideoUrls);
						return newData;
					});

					url = BASE_URL + "index.php/search_lookup/get_categories";
					if (ApplicationData.enableAdmin) {
						url += "/false";
					}
					res = await getDataWithFetch(url);
					setChoicePairsList(res);
					ApplicationData.choicePairsList = res;

					if (ApplicationData.siteClosedFlag === false) {
						url = BASE_URL + "index.php/misc/get_intl";
						// console.log('DEBUG:', url);
						res = await getDataWithFetch(url);
						ApplicationData.intl = res;

						// console.log('enableStripe', enableStripe);
						if (enableStripe) {
							// console.log('DEBUG', `BASE_URL = `, BASE_URL);
							url = BASE_URL + 'index.php/stripe_util/config';
							// console.log('DEBUG', `url = `, url);
							const stripeConfig = await fetch(url).then((r) => r.json());
							// console.log('DEBUG', `index.php/stripe_util/config -- json = `, stripeConfig);
							const {publishableKey} = stripeConfig;
							// console.log('DEBUG', `publishableKey = `, publishableKey);
							const stripePromise = loadStripe(publishableKey);
							setStripePromise(stripePromise);
						}
					}
				}
			}
        })();

		ApplicationData.BASE_URL = BASE_URL;
	}, [BASE_URL, enableStripe]);

    // Memoized function using useCallback
    ApplicationData.memoizedGetTagLabelInfo = useCallback((input) => {
		// If the cache has not yet beent created then creat it.  First time it gets called if it gets here 
		// first.
		if (!ApplicationData.cache) {
			ApplicationData.cache = new Map();
		}

		if (!ApplicationData.tagLabelLocalised) {
			ApplicationData.tagLabelLocalised = ["To Open", "To Close"];
		}

		// Check if the input exists in the cache
        if (ApplicationData.cache.has(input)) {
			const result = ApplicationData.cache.get(input);
			// console.log('DEBUG', "App - get_region_titles returns cached value:  ", result);
			ApplicationData.tagLabelLocalised = result;
            return;
        }

        // If not in cache, fetch from network
        if (BASE_URL) {
            (async () => {
                // console.log('DEBUG: App - BASE_URL = ', BASE_URL);
                const result = await getTagLabelInfo(BASE_URL, ApplicationData.lang || 'en');
				
                if (result?.status) {
					// console.log('DEBUG', 'App - get_region_titles status = ', result?.status);
					// console.log('DEBUG', `App - get_region_titles result = `, result.result);

					ApplicationData.cache.set(input, result.result);
					ApplicationData.tagLabelLocalised = result.result;
					// console.log('DEBUG: App - tagLabelLocalised', ApplicationData.tagLabelLocalised);
                }
            })();
        }
    }, [BASE_URL]);

	const tagLabelInfoLoaded = useRef(false);
	useEffect(() => {
		if (tagLabelInfoLoaded.current === false) {
			tagLabelInfoLoaded.current = true;
			ApplicationData.lang = getLocalStorage('CURRENT_LANG');
			ApplicationData.memoizedGetTagLabelInfo(ApplicationData.lang);
		}
	}, [BASE_URL]);
	const [authToken, setAuthToken] = useState(getLocalStorage('token'));

	// Check admin password status on component mount
	useEffect(() => {
		const checkAdminPasswordStatus = async () => {
			const userName = getLocalStorage("username");
			if (BASE_URL && userName) {
				try {
					// Check the vesrion in the database and pull it here.
					// If version is greater than initial or defined, password has been changed
					// If not, then we need to force a password change before allowing access.
					// This is only for the admin user.
					// console.log('DEBUG:  Checking admin password status for user ', userName);
					const url = `${BASE_URL}index.php/user/check_admin_password_version/${userName}`;
					const json = await getDataWithFetch(url);
					
					// If version is greater than initial or defined, password has been changed
					const versionStore = parseInt(getLocalStorage("version") || "0");

					if (json?.version > versionStore) {
						setLocalStorage("version", json?.version);
						// setIsPasswordChanged(true);
						// console.log('Admin password has been changed.');

						// Force logout if password hasn't been changed
						localStorage.removeItem('token');
						localStorage.removeItem('username');
						localStorage.removeItem('is_premium');
						localStorage.removeItem('version');
					}
					else {
						// console.log('Admin password has NOT been changed.');
						// setIsPasswordChanged(false);
					}
				} catch (error) {
					console.error('Error checking admin password status:', error);
					// setIsPasswordChanged(false);
				}
			}
			// setIsCheckingPassword(false);
		};

		// console.log('DEBUG:  Checking admin password status on App load');
		checkAdminPasswordStatus();
	}, [BASE_URL]);

	return (
		<ApplicationContext.Provider value={{...ApplicationData, authToken, setAuthToken}}>
			{enableStripe ? (<>
				<Elements stripe={stripePromise}>
					{renderPage()}
				</Elements>
			</>): (<>
				{renderPage()}
			</>)}
		</ApplicationContext.Provider>
	);
};

export default App;
